import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Loader2, Mail } from 'lucide-react';
import { ApiError } from '../../lib/apiClient';
import { selfServiceSupabase } from '../../lib/selfServiceSupabase';
import Banner from '../../components/self-service/shared/Banner';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';
import CtvBuyerAuthShell, {
  CTV_NAVY,
  CTV_CTA_ACCENT,
  CTV_CTA_DEEP,
} from '../../components/self-service/auth/CtvBuyerAuthShell';
import {
  stationTeamService,
  type StationInviteView,
} from '../../services/stationTeamService';

/**
 * Landing page for `/station-invites/:token`.
 *
 * No Supabase magic-link involvement: the email CTA points directly here,
 * and we collect a password from the invitee, post to our own
 * `accept-with-password` endpoint, then sign them in. Avoids the redirect-
 * URL allow-list, OTP single-use, and cross-host session failure modes
 * that broke the magic-link flow.
 */
const StationInviteAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invite, setInvite] = React.useState<StationInviteView | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token) {
      setError('Missing invitation token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await stationTeamService.lookupInvite(token);
        if (!cancelled) setInvite(data);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : 'Failed to load invitation.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!token || !invite) return;
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setSubmitError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await stationTeamService.acceptInviteWithPassword(
        token,
        password,
      );
      if (!selfServiceSupabase) {
        throw new Error('Supabase is not configured.');
      }
      // Now sign in with the password we just set so the JWT picks up the
      // new role + grant claims.
      const { error: signInErr } =
        await selfServiceSupabase.auth.signInWithPassword({
          email: res.email,
          password,
        });
      if (signInErr) throw signInErr;
      navigate(`/admin/${res.stationSlug}`, { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to accept invitation.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invitation…
      </div>
    );
  }

  if (error || !invite) {
    return (
      <CtvBuyerAuthShell variant="dark">
        <div className="w-full bg-white rounded-[20px] shadow-2xl p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2" style={{ color: CTV_NAVY }}>
            Invitation unavailable
          </h1>
          <p className="text-slate-600 text-sm mb-6">
            {error ?? 'This invitation could not be loaded.'}
          </p>
          <PrimaryButton
            type="button"
            onClick={() => navigate('/')}
            accentColor={CTV_CTA_ACCENT}
            primaryColor={CTV_CTA_DEEP}
          >
            Back to home
          </PrimaryButton>
        </div>
      </CtvBuyerAuthShell>
    );
  }

  if (invite.status === 'accepted') {
    return (
      <CtvBuyerAuthShell variant="dark">
        <div className="w-full bg-white rounded-[20px] shadow-2xl p-8 text-center">
          <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2" style={{ color: CTV_NAVY }}>
            Invitation already accepted
          </h1>
          <p className="text-slate-600 text-sm mb-6">
            You're already on the {invite.stationName} team. Sign in to access
            the admin dashboard.
          </p>
          {invite.stationSlug && (
            <PrimaryButton
              type="button"
              onClick={() => navigate(`/admin/${invite.stationSlug}`)}
              accentColor={CTV_CTA_ACCENT}
              primaryColor={CTV_CTA_DEEP}
            >
              Open admin dashboard
            </PrimaryButton>
          )}
        </div>
      </CtvBuyerAuthShell>
    );
  }

  if (invite.status === 'revoked' || invite.status === 'expired') {
    return (
      <CtvBuyerAuthShell variant="dark">
        <div className="w-full bg-white rounded-[20px] shadow-2xl p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-slate-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2" style={{ color: CTV_NAVY }}>
            Invitation {invite.status}
          </h1>
          <p className="text-slate-600 text-sm">
            This invitation is no longer valid. Ask the inviter to send a new
            one.
          </p>
        </div>
      </CtvBuyerAuthShell>
    );
  }

  return (
    <CtvBuyerAuthShell variant="dark">
      <div className="w-full bg-white rounded-[20px] shadow-2xl p-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: CTV_NAVY }}>
          You've been invited
        </h1>
        <p className="text-slate-600 text-sm mb-6">
          {invite.inviterEmail
            ? `${invite.inviterEmail} invited you to join the `
            : 'You were invited to join the '}
          <span className="font-semibold text-slate-900">
            {invite.stationName}
          </span>{' '}
          team on Streamline as a{' '}
          <span className="font-semibold text-slate-900">
            {invite.role === 'manager' ? 'Manager' : 'Sales Rep'}
          </span>
          .
        </p>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 text-sm">
          <div className="flex items-center gap-2 text-slate-700">
            <Mail className="w-4 h-4 text-slate-500" />
            <span>
              Account Email:{' '}
              <span className="font-medium text-slate-900">{invite.email}</span>
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block">
            <span className="text-xs text-slate-500 uppercase tracking-wide">
              Choose a password
            </span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7e80ff]"
            />
            <span className="mt-1 block text-xs text-slate-400">
              At least 8 characters.
            </span>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 uppercase tracking-wide">
              Confirm password
            </span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(ev) => setConfirm(ev.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7e80ff]"
            />
          </label>
          {submitError && (
            <Banner tone="warn" label="Couldn't accept">
              {submitError}
            </Banner>
          )}
          <PrimaryButton
            type="submit"
            disabled={submitting || !password || !confirm}
            accentColor={CTV_CTA_ACCENT}
            primaryColor={CTV_CTA_DEEP}
            fullWidth
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Accept and Continue
          </PrimaryButton>
        </form>
      </div>
    </CtvBuyerAuthShell>
  );
};

export default StationInviteAcceptPage;
